import { ApiService, type PaginatedResponse } from '@sparklayer-io/spark-dash-core/types/api';
import type { PaginationOptions } from '@sparklayer-io/spark-dash-ui/utils/pagination';
import type { CustomerLookup, CustomerSearchQuery } from '../types/api';
import type { Customer } from '../types/customer';
import { CustomerApi, type CustomerApiContract } from './customer.api';
import { PlatformCustomerApi, type PlatformCustomerApiContract } from './platform-customer.api';
import { CustomerRolesApi, type CustomerRolesApiContract } from './roles.api';

export interface CustomersApiContract {
  customer(lookup: CustomerLookup): CustomerApiContract;
  search(
    query: CustomerSearchQuery,
    pagination: PaginationOptions,
  ): Promise<PaginatedResponse<Customer>>;
  salesAgentsUsedCount(): Promise<{
    total: number;
  }>;
  salesAgentGroups(): Promise<string[]>;
  roles(): CustomerRolesApiContract;
  platformCustomer(): PlatformCustomerApiContract;
}

declare module '@sparklayer-io/spark-dash-core/context/page-context' {
  interface Apis {
    customers: CustomersApiContract;
  }
}

/**
 * A service to interact with customers via the SparkLayer API.
 */
export class CustomersApi extends ApiService implements CustomersApiContract {
  public customer(lookup: CustomerLookup): CustomerApiContract {
    return new CustomerApi(this.api, lookup);
  }

  public search(
    query: CustomerSearchQuery,
    pagination: PaginationOptions,
  ): Promise<PaginatedResponse<Customer>> {
    const params = pagination.toQueryParams();

    return this.api.proxy({
      method: 'POST',
      path: `/v2/search-customers?${params}`,
      payload: query,
    });
  }

  public salesAgentsUsedCount(): Promise<{
    total: number;
  }> {
    return this.api.proxy({
      method: 'GET',
      path: '/v2/fetch-sales-agent-count',
    });
  }
  public salesAgentGroups(): Promise<string[]> {
    return this.api.proxy({
      method: 'GET',
      path: '/v2/sales-agent-groups',
    });
  }

  public roles(): CustomerRolesApiContract {
    return new CustomerRolesApi(this.api);
  }

  public platformCustomer(): PlatformCustomerApiContract {
    return new PlatformCustomerApi(this.api);
  }
}
